import { useQueryClient } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo, CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { ControleCarregando } from '@/componentes/Esqueleto';
import { interpretarNumero } from '@/lib/formato';
import { primeiraMaiuscula } from '@/lib/texto';
import { ehFichaAlterada } from '@/lib/versao-da-ficha';

import { useSessao } from '@/modulos/sessao/consultas';

import { apoioLigado } from './apoio';
import { CampoDoCodigo } from './CampoDoCodigo';
import { CHAVE_FROTA, lerFichaAtual, useAjustarFicha, useCadastrosDeApoio, useTiposDeEquipamento } from './consultas-frota';
import {
  erroDaPlaca,
  erroDoAno,
  erroDoAnoAquisicao,
  erroDoAnoModelo,
  erroDoCodigoInterno,
  erroDoRenavam,
  fichaInicial,
  montarAjuste,
  refazerSobreAFichaNova,
  type EstadoDaFicha,
} from './ficha';
import { COMBUSTIVEIS, type EquipamentoDaFrota } from './frota';
import { MensagemDeErro } from './MensagemDeErro';
import { AJUDA_DO_CODIGO_NA_FICHA, opcoesDeTipo, rotuloDoTipoDoModelo, tipoDaLista } from './tipos';
import { opcaoDaConfiguracao } from '@/modulos/equipamentos/opcoesComVisual';
import { CaixaAbasteceEmPosto } from './CaixaAbasteceEmPosto';

/**
 * Editar a ficha depois do cadastro (melhoria pedida pelo dono em 24/09).
 *
 * As duas regras — abrir com o que e DESTA maquina, e nao com o que ela herda;
 * e campo em branco ir para `limpar` e voltar a herdar — moram em `ficha.ts`,
 * puras, com teste ao lado.
 */
export function FormularioFicha({
  equipamento,
  aoTerminar,
}: {
  readonly equipamento: EquipamentoDaFrota;
  readonly aoTerminar: () => void;
}) {
  const base = useId();
  const sessao = useSessao();
  const apoio = useCadastrosDeApoio(apoioLigado(sessao.data?.permissoes, 'equipamento.gerir'));
  const tipos = useTiposDeEquipamento(sessao.data?.permissoes.includes('equipamento.ler') ?? false);
  const ajustar = useAjustarFicha();
  const cliente = useQueryClient();
  const [ficha, setFicha] = useState<EstadoDaFicha>(() => fichaInicial(equipamento));
  /* A FICHA E A VERSAO DE QUANDO O FORMULARIO ABRIU (robos de 20/10/2026,
     achado 1). Guardadas aqui, e nao lidas do `equipamento` a cada render: a
     lista relida traria a versao nova, e o formulario velho gravaria por cima
     com ela. So mudam quando a tela refaz o formulario sobre a ficha nova. */
  const [aberta, setAberta] = useState<EstadoDaFicha>(() => fichaInicial(equipamento));
  const [versao, setVersao] = useState<string | null>(equipamento.versao);
  const [refeito, setRefeito] = useState<{ readonly titulo: string; readonly frase: string } | null>(null);
  const mudar = (campo: keyof EstadoDaFicha) => (valor: string) => {
    setFicha((agora) => ({ ...agora, [campo]: valor }));
  };

  /* O tipo do MODELO, para a opção "Igual ao modelo" (0124): o da configuração
     escolhida agora; sem ela na lista, o que a máquina herda hoje. */
  const tipoDoModelo =
    apoio.data?.configuracoes.find((c) => c.id === ficha.configuracaoId)?.tipo ??
    (equipamento.origemDoTipo === 'modelo' ? equipamento.tipo : null);
  /* O tipo gravado FORA da lista (0131): fica entre as opções, para gravar
     sem mexer não o apagar, e a ajuda pede para trocar. */
  const tipoForaDaLista = ficha.tipo !== '' && tipos.data !== undefined && tipoDaLista(tipos.data, ficha.tipo) === null;

  const ajuste = montarAjuste(ficha);
  const podeGravar = ajuste !== null && !ajustar.isPending;

  const nomeDoValor = (campo: keyof EstadoDaFicha, valor: string): string => {
    if (campo === 'configuracaoId') return apoio.data?.configuracoes.find((c) => c.id === valor)?.rotulo ?? valor;
    if (campo === 'filialId') return apoio.data?.filiais.find((f) => f.id === valor)?.rotulo ?? valor;
    return valor;
  };

  /* Outra pessoa gravou depois que o formulario abriu: o servidor recusou sem
     gravar nada. A tela rele a maquina e refaz o formulario sobre a versao
     nova — o que so a outra pessoa mudou vem com o valor dela, o que voce
     digitou fica — e diz quais. Gravar de novo vai com a versao nova. */
  async function refazerSobreANova(erro: unknown) {
    if (!ehFichaAlterada(erro)) return;
    void cliente.invalidateQueries({ queryKey: CHAVE_FROTA });
    const nova = await lerFichaAtual(equipamento.id).catch(() => null);
    if (!nova) return;
    const r = refazerSobreAFichaNova(ficha, aberta, nova, nomeDoValor);
    setFicha(r.estado);
    setAberta(r.base);
    setVersao(nova.versao);
    setRefeito({ titulo: erro instanceof Error ? primeiraMaiuscula(erro.message) : 'A ficha mudou', frase: r.frase });
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (!ajuste || ajustar.isPending) return;
    setRefeito(null);
    ajustar.mutate({ id: equipamento.id, ajuste, versao }, { onSuccess: aoTerminar, onError: (e) => void refazerSobreANova(e) });
  }

  return (
    /* A FICHA EM GRADE (pedido do dono, 31/10/2026). O painel abre expandido,
       e o formulario e a `formulario-grade` do manual (p.39): ate tres colunas,
       secoes com titulo e a barra de salvar FLUTUANDO no pe do corpo que rola.
       A grade mede a propria largura, e por isso a mesma ficha e uma coluna se
       a pessoa reduzir o painel. Erro de campo aparece ao sair dele (p.23). */
    <form onSubmit={enviar} className="flex flex-col gap-4" aria-label={`Editar a ficha de ${equipamento.codigo}`}>
      <p className="txt-apoio text-tinta-secundaria">
        Campo em branco volta a herdar do catálogo. A alteração fica na trilha, com quem mudou e quando.
      </p>

      <div className="formulario-grade">
      <h3 className="campo-linha txt-subtitulo text-tinta">Identificação</h3>

      {/* O CÓDIGO É DO SISTEMA (0131, decisão 57): mostrado, nunca editado.
          Trocar o tipo não o muda. */}
      <CampoDoCodigo valor={ficha.codigo} ajuda={AJUDA_DO_CODIGO_NA_FICHA} />
      {/* O SEGUNDO CÓDIGO (0125): o nome da máquina no dia a dia da empresa.
          Não é o patrimônio, que é o número do controle patrimonial. */}
      <CampoTexto
        rotulo="Código interno"
        opcional
        valor={ficha.codigoInterno}
        aoMudar={mudar('codigoInterno')}
        maxLength={40}
        placeholder="CB-07"
        ajuda="Como a empresa chama a máquina no dia a dia. Letras maiúsculas, números e traços."
        validar={erroDoCodigoInterno}
      />
      <CampoTexto
        rotulo="Patrimônio"
        opcional
        valor={ficha.patrimonio}
        aoMudar={mudar('patrimonio')}
        maxLength={40}
        ajuda="O número do controle patrimonial da empresa."
      />
      <div className="campo-largo">
        <CampoTexto
          rotulo="Descrição"
          valor={ficha.descricao}
          aoMudar={mudar('descricao')}
          validar={(v) => (v.trim() ? null : 'Escreva a descrição da máquina para poder gravar.')}
        />
      </div>
      <CampoTexto
        rotulo="Número de série"
        opcional
        valor={ficha.numeroSerie}
        aoMudar={mudar('numeroSerie')}
        ajuda="Está na plaqueta de identificação da máquina. Não se repete dentro da empresa."
      />
      {/* As opcoes ainda vem (p.32): sem elas a lista mostrava "Sem
          configuração" para a maquina que tem uma. O rotulo fica; so o
          controle vira a barra de 40 px. */}
      <div className="campo-largo">
        {apoio.isPending ? (
          <Campo id={`${base}-configuracao`} rotulo="Configuração" opcional>
            <ControleCarregando oQue="Carregando as configurações" />
          </Campo>
        ) : (
          <CampoSelecao
            rotulo="Configuração"
            opcional
            valor={ficha.configuracaoId}
            aoMudar={mudar('configuracaoId')}
            rotuloVazio="Sem configuração"
            opcoes={(apoio.data?.configuracoes ?? []).map((c) => opcaoDaConfiguracao(c, c.rotulo))}
          />
        )}
      </div>
      {apoio.isPending ? (
        <Campo id={`${base}-filial`} rotulo="Filial" opcional>
          <ControleCarregando oQue="Carregando as filiais" />
        </Campo>
      ) : (
        <CampoSelecao
          rotulo="Filial"
          opcional
          valor={ficha.filialId}
          aoMudar={mudar('filialId')}
          rotuloVazio="Sem filial"
          opcoes={(apoio.data?.filiais ?? []).map((f) => ({ valor: f.id, rotulo: f.rotulo }))}
        />
      )}

      <h3 className="campo-linha txt-subtitulo text-tinta">Especificação</h3>
      {/* O TIPO DESTA MAQUINA (0124, pedido do dono em 26/10/2026). Ele herda
          do MODELO e por isso não estava aqui: dois caminhões do mesmo chassi
          diziam "Caminhão basculante" porque o chassi é o mesmo, mas um leva
          caçamba e o outro tanque de água. Escolher é dizer "esta unidade é
          de outro tipo"; "Igual ao modelo" volta a herdar (vai em `limpar`).

          Da LISTA desde a 0131 (decisão 57, pedido do dono em 29/10/2026): a
          mesma da planilha de importação, e o banco recusa tipo fora dela. */}
      {tipos.isLoading ? (
        <Campo id={`${base}-tipo`} rotulo="Tipo" opcional>
          <ControleCarregando oQue="Carregando os tipos de máquina" />
        </Campo>
      ) : (
        <CampoSelecao
          rotulo="Tipo"
          opcional
          valor={ficha.tipo}
          aoMudar={mudar('tipo')}
          rotuloVazio={rotuloDoTipoDoModelo(tipoDoModelo)}
          opcoes={opcoesDeTipo(tipos.data ?? [], aberta.tipo)}
          {...(tipos.isError
            ? { erro: 'Não foi possível carregar a lista de tipos. Feche a ficha e abra de novo para tentar outra vez.' }
            : {
                ajuda: tipoForaDaLista
                  ? `O tipo "${ficha.tipo}" está fora da lista. Escolha um da lista, ou Igual ao modelo. O código não muda.`
                  : 'Escolha outro só quando esta máquina for de outro tipo que o modelo. O código não muda.',
              })}
        />
      )}
      <CampoTexto
        rotulo="Motor"
        opcional
        valor={ficha.motorModelo}
        aoMudar={mudar('motorModelo')}
        ajuda={
          equipamento.origemDoMotor === 'configuracao'
            ? `Em branco, herda o motor da configuração (${equipamento.motorModelo ?? 'não informado'}).`
            : 'Em branco, herda o motor da configuração. Preencha só quando esta máquina estiver com outro.'
        }
      />
      <CampoTexto
        rotulo="Número de série do motor"
        opcional
        valor={ficha.motorNumeroSerie}
        aoMudar={mudar('motorNumeroSerie')}
        ajuda="Está na plaqueta do motor. Não se repete dentro da empresa."
      />
      <CampoTexto
        rotulo="Ano de fabricação"
        opcional
        valor={ficha.ano}
        aoMudar={mudar('ano')}
        validar={erroDoAno}
      />
      <CampoTexto
        rotulo="Ano do modelo"
        opcional
        valor={ficha.anoModelo}
        aoMudar={mudar('anoModelo')}
        ajuda="O de fabricação ou o seguinte."
        validar={(v) => erroDoAnoModelo(v, ficha.ano)}
      />
      <CampoTexto
        rotulo="Ano de aquisição"
        opcional
        valor={ficha.anoAquisicao}
        aoMudar={mudar('anoAquisicao')}
        validar={(v) => erroDoAnoAquisicao(v, ficha.ano)}
      />
      <CampoSelecao
        rotulo="Combustível"
        opcional
        valor={ficha.combustivel}
        aoMudar={mudar('combustivel')}
        rotuloVazio="Não informado"
        opcoes={COMBUSTIVEIS}
      />
      <CampoTexto
        rotulo="Capacidade do tanque"
        opcional
        valor={ficha.capacidade}
        aoMudar={mudar('capacidade')}
        ajuda={
          equipamento.origemDaCapacidade === 'configuracao' && equipamento.capacidadeTanqueL !== null
            ? `Em litros. Em branco, herda da configuração (${String(equipamento.capacidadeTanqueL).replace('.', ',')} L).`
            : 'Em litros. Em branco, a máquina herda a capacidade da configuração.'
        }
        validar={(v) => {
          if (v.trim() === '') return null;
          const n = interpretarNumero(v);
          return n !== null && n > 0 ? null : 'Informe a capacidade em litros, ou deixe em branco.';
        }}
      />
      <CampoSelecao
        rotulo="Medidor"
        opcional
        valor={ficha.medidor}
        aoMudar={mudar('medidor')}
        rotuloVazio="Herda da configuração"
        opcoes={[
          { valor: 'horimetro', rotulo: 'Horímetro' },
          { valor: 'odometro', rotulo: 'Odômetro' },
        ]}
      />

      <h3 className="campo-linha txt-subtitulo text-tinta">Documento e abastecimento</h3>
      <CampoTexto
        rotulo="Placa"
        opcional
        valor={ficha.placa}
        aoMudar={mudar('placa')}
        maxLength={10}
        ajuda="ABC1234 ou, no padrão Mercosul, ABC1D23. Grava em maiúsculas e sem hífen."
        validar={erroDaPlaca}
      />
      <CampoTexto
        rotulo="Renavam"
        opcional
        valor={ficha.renavam}
        aoMudar={mudar('renavam')}
        maxLength={14}
        ajuda="Os 11 dígitos do documento. Com 9 ou 10, os zeros à esquerda se completam."
        validar={erroDoRenavam}
      />
      {/* 0133 (decisao 59): e quem administra a frota que diz qual maquina
          abastece fora dos tanques da empresa. So a marcada aparece na lista
          do posto parceiro, e o banco recusa o lancamento dele na outra. */}
      <CaixaAbasteceEmPosto
        id={`${base}-abastece-em-posto`}
        marcada={ficha.abasteceEmPosto === 'sim'}
        aoMudar={(marcada) => {
          mudar('abasteceEmPosto')(marcada ? 'sim' : '');
        }}
      />
      </div>

      {refeito ? (
        /* A recusa do servidor (quem alterou e quando) e o que a tela fez com
           ela, no lugar da mensagem de erro: "reabra" ja foi feito. */
        <div role="alert" className="bloco-rebaixado flex flex-col gap-1 p-3">
          <span className="txt-corpo text-tinta">{refeito.titulo}</span>
          <span className="txt-apoio text-tinta-secundaria">{refeito.frase}</span>
        </div>
      ) : (
        <MensagemDeErro erro={ajustar.error} />
      )}

      {/* A BARRA DE SALVAR FLUTUANTE do manual (p.30, p.39), no pe do corpo
          das abas, que e o que rola: com a ficha em tres colunas ela fica a
          vista o tempo todo, e gravar nao pede rolar ate o fim. Descartar a
          esquerda, o primario do painel a direita. */}
      <div className="barra-de-acoes">
        <Botao nivel="fantasma" type="button" onClick={aoTerminar}>
          Descartar alterações
        </Botao>
        <Botao nivel="primario" type="submit" disabled={!podeGravar} carregando={ajustar.isPending}>
          {ajustar.isPending ? 'Gravando' : 'Gravar ficha'}
        </Botao>
      </div>
    </form>
  );
}
